// src/components/ModalItinerary.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, ButtonGroup } from "react-bootstrap";
import { MapContainer, TileLayer } from "react-leaflet";
import L from "leaflet";
import { toast } from "react-toastify";
import { useTranslate } from "../../hooks/LanguageProvider";
import RouteSimulator from "../Map/RouteSimulator";
import FullscreenControlCustom from "../../components/Map/FullscreenControlCustom";
import ItineraryLayerDataParking from "../Map/ItineraryLayerParking";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ModalItineraryProps {
  show: boolean;
  onHide: () => void;
  psn: string | null;
  fromDate: string | null;
  toDate: string | null;
}

const ModalItinerary: React.FC<ModalItineraryProps> = ({
  show,
  onHide,
  psn,
  fromDate,
  toDate,
}) => {
  const [points, setPoints] = useState<any[]>([]);
  const [simPlaying, setSimPlaying] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const { translate } = useTranslate();

  // Chargement des données
  useEffect(() => {
    if (!show || !psn || !fromDate || !toDate) {
      setPoints([]);
      return;
    }

    const fetchItinerary = async () => {
      try {
        const res = await fetch(
          `${backendUrl}/api/map/reports/date/1/${psn}/${fromDate}/${toDate}`
        );
        if (!res.ok) throw new Error("Network error");

        const data = await res.json();
        const pts = data?.data ?? [];

        if (pts.length === 0) {
          toast.info("No itinerary data");
          setPoints([]);
        } else {
          setPoints(pts);
          toast.success(`${pts.length} points loaded`, { autoClose: 2000 });
        }
      } catch (err) {
        toast.error("Error loading itinerary");
        setPoints([]);
      }
    };

    fetchItinerary();
  }, [show, psn, fromDate, toDate]);

  const simPath = useMemo<[number, number][]>(() => {
    return points
      .filter((p: any) => typeof p.LAT === "number" && (p.LNG || p.LON))
      .map((p: any) => [p.LAT, (p.LNG ?? p.LON) as number]);
  }, [points]);

  const hasData = points.length > 0;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered fullscreen="xl-down">
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>
          <i className="ri-route-line me-2"></i>
          {translate("Itinerary")} {psn && `• ${psn}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 position-relative" style={{ height: "84vh" }}>
        <MapContainer
          center={[28.5, 1.5]}
          zoom={6}
          zoomControl={false}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          {/* Fond de carte selon le mode */}
          <TileLayer
            url={
              darkMode
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            }
            attribution={
              darkMode
                ? '&copy; <a href="https://carto.com/">CARTO</a>'
                : '&copy; OpenStreetMap'
            }
          />

          <FullscreenControlCustom position="topright" />

          {/* Couche principale – tout est géré ici */}
          {hasData && (
            <ItineraryLayerDataParking
              points={points}
              visible={true}
              color="#00ffaa"
              autoFit={true}
            />
          )}

          {/* Simulateur contrôlable */}
          {hasData && simPath.length >= 2 && (
            <RouteSimulator
              path={simPath}
              speed={120}
              playing={simPlaying}
              autofit={false}
              right={true}
              iconSize={38}
             
            />
          )}
        </MapContainer>

        {/* Contrôles en bas */}
        <div
          className="position-absolute bottom-0 start-0 end-0 p-3"
          style={{
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(4px)",
            zIndex: 1000,
          }}
        >
          <div className="d-flex justify-content-between align-items-center text-white">
            {/* Légende */}
            <div className="small">
              <span className="me-3">
                <i className="fas fa-circle text-success me-1"></i> Moteur arrêté
              </span>
              <span className="me-3">
                <i className="fas fa-circle text-warning me-1"></i> Parking moteur allumé
              </span>
              <span>
                <i className="fas fa-route text-cyan me-1"></i> Trajet
              </span>
            </div>

            {/* Boutons */}
            <ButtonGroup size="sm">
              <Button
                variant={simPlaying ? "success" : "secondary"}
                onClick={() => setSimPlaying(!simPlaying)}
              >
                <i className={`fas fa-${simPlaying ? "pause" : "play"}`}></i>
              </Button>
              <Button
                variant={darkMode ? "light" : "dark"}
                onClick={() => setDarkMode(!darkMode)}
              >
                <i className={`fas fa-${darkMode ? "sun" : "moon"}`}></i>
              </Button>
              <Button variant="danger" onClick={onHide}>
                {translate("Close")}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ModalItinerary;